import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormControl,
  Select,
  MenuItem,
  Button,
  Box,
  Divider,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { useTranslation } from "react-i18next";
import {
  grayColor,
  whiteColor,
} from "../../assets/jss/material-dashboard-pro-react";

const styles = (clientColor) => ({
  routeFilterWrapper: {
    position: "relative",
    backgroundColor: clientColor,
    borderBottom: `1px solid ${grayColor[12]}`,
    flexShrink: 0,
    zIndex: 1000,
  },
  truncatedLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    color: whiteColor,
    gap: "8px",
    backgroundColor: clientColor,
    "&:hover": {
      opacity: 0.9,
    },
  },
  nodeText: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  nodeLabel: {
    fontWeight: "600",
    color: whiteColor,
  },
  nodeLocation: {
    color: whiteColor,
    fontStyle: "italic",
    opacity: 0.85,
  },
  arrow: {
    color: whiteColor,
    fontSize: "16px",
    opacity: 0.7,
    display: "flex",
    alignItems: "center",
  },
  expandIcon: {
    fontSize: "18px",
    color: whiteColor,
    marginLeft: "8px",
    display: "flex",
    alignItems: "center",
  },
  expandedBox: {
    position: "absolute",
    top: "100%",
    left: "5%",
    width: "90%",
    padding: "16px",
    backgroundColor: whiteColor,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: "8px",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
    pointerEvents: "auto",
    userSelect: "none",
    cursor: "default",
    "& *": {
      userSelect: "auto",
    },
  },
  columnsContainer: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr auto 1fr",
    gap: "16px",
    marginBottom: "16px",
    alignItems: "start",
  },
  filterColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  columnTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: grayColor[0],
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  divider: {
    height: "100%",
    alignSelf: "stretch",
  },
  formControl: {
    width: "100%",
    "& > div": {
      fontSize: "12px",
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "8px",
  },
  clearButton: {
    fontSize: "11px",
    color: grayColor[1],
    borderColor: grayColor[4],
    "&:hover": {
      backgroundColor: grayColor[11],
    },
  },
  applyButton: {
    fontSize: "11px",
    color: whiteColor,
    backgroundColor: clientColor,
    "&:hover": {
      backgroundColor: clientColor,
      opacity: 0.9,
    },
  },
});

const RouteFilter = (props) => {
  const {
    clientColor,
    filterOptions,
    currentFilters,
    onApplyFilters,
    onClearFilters,
  } = props;

  const useStyles = makeStyles(() => styles(clientColor));
  const classes = useStyles();
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.filters.route_filter",
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    node1_reference_productionsite: "",
    node1_location: "",
    node2_reference_client: "",
    node2_location: "",
    node3_reference_pointofsale: "",
    node3_location: "",
  });

  // Define placeholder labels (single source of truth for both selects and truncated view)
  const placeholderLabels = {
    node1_reference_productionsite: t("production_site"),
    node1_location: t("all_locations"),
    node2_reference_client: t("client"),
    node2_location: t("all_locations"),
    node3_reference_pointofsale: t("point_of_sale"),
    node3_location: t("all_locations"),
  };

  // Helper function to get display text for filters
  const getDisplayText = (filterKey) => {
    const value = currentFilters[filterKey];
    // Handle undefined, null, empty string, and 0
    if (value === undefined || value === null || value === "" || value === 0) {
      return placeholderLabels[filterKey];
    }
    return value;
  };

  // Click outside to close
  const expandedBoxRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isExpanded &&
        expandedBoxRef.current &&
        !expandedBoxRef.current.contains(event.target)
      ) {
        // Check if click is on a Material-UI Menu/Popover (dropdown options)
        // These are rendered in portals and appear outside our ref
        const isClickOnDropdown =
          event.target.closest(".MuiPopover-root") ||
          event.target.closest(".MuiMenu-root") ||
          event.target.closest('[role="presentation"]');

        if (isClickOnDropdown) {
          // Don't close if clicking on dropdown menu
          return;
        }

        // Check if click is not on the truncated line itself
        const truncatedLine = expandedBoxRef.current.previousSibling;
        if (truncatedLine && !truncatedLine.contains(event.target)) {
          setIsExpanded(false);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isExpanded]);

  // Update temp filters when currentFilters prop changes, but only when popover is closed
  // This prevents resetting user selections while they're actively using the filters
  React.useEffect(() => {
    if (!isExpanded) {
      setTempFilters({
        node1_reference_productionsite:
          currentFilters.node1_reference_productionsite || "",
        node1_location: currentFilters.node1_location || "",
        node2_reference_client: currentFilters.node2_reference_client || "",
        node2_location: currentFilters.node2_location || "",
        node3_reference_pointofsale:
          currentFilters.node3_reference_pointofsale || "",
        node3_location: currentFilters.node3_location || "",
      });
    }
  }, [currentFilters, isExpanded]);

  const handleTempFilterChange = (filterKey, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  const handleApply = () => {
    setIsExpanded(false);
    onApplyFilters(tempFilters);
  };

  const handleClear = () => {
    const clearedFilters = {
      node1_reference_productionsite: "",
      node1_location: "",
      node2_reference_client: "",
      node2_location: "",
      node3_reference_pointofsale: "",
      node3_location: "",
    };
    setTempFilters(clearedFilters);
    setIsExpanded(false);
    onClearFilters();
  };

  return (
    <div className={classes.routeFilterWrapper}>
      {/* Truncated Line - Using grid to match expanded layout */}
      <div
        className={classes.truncatedLine}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Column 1: Node 1 */}
        <div className={classes.nodeText}>
          <span className={classes.nodeLabel}>
            {getDisplayText("node1_reference_productionsite")}
          </span>
          <span className={classes.nodeLocation}>
            ({getDisplayText("node1_location")})
          </span>
        </div>

        {/* Divider/Arrow 1 */}
        <div className={classes.arrow}>
          <ArrowForwardIcon style={{ fontSize: "16px" }} />
        </div>

        {/* Column 2: Node 2 */}
        <div className={classes.nodeText}>
          <span className={classes.nodeLabel}>
            {getDisplayText("node2_reference_client")}
          </span>
          <span className={classes.nodeLocation}>
            ({getDisplayText("node2_location")})
          </span>
        </div>

        {/* Divider/Arrow 2 */}
        <div className={classes.arrow}>
          <ArrowForwardIcon style={{ fontSize: "16px" }} />
        </div>

        {/* Column 3: Node 3 */}
        <div className={classes.nodeText}>
          <span className={classes.nodeLabel}>
            {getDisplayText("node3_reference_pointofsale")}
          </span>
          <span className={classes.nodeLocation}>
            ({getDisplayText("node3_location")})
          </span>
        </div>

        {/* Expand/Collapse Icon */}
        <div className={classes.expandIcon}>
          {isExpanded ? (
            <ExpandMoreIcon style={{ fontSize: "18px" }} />
          ) : (
            <ChevronRightIcon style={{ fontSize: "18px" }} />
          )}
        </div>
      </div>

      {/* Expanded Filter Box - Popover */}
      {isExpanded && (
        <>
          <Box
            className={classes.expandedBox}
            ref={expandedBoxRef}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className={classes.columnsContainer}>
              {/* Column 1: Node 1 Filters */}
              <div className={classes.filterColumn}>
                <div className={classes.columnTitle}>{t("node_1_title")}</div>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node1_reference_productionsite}
                    onChange={(e) =>
                      handleTempFilterChange(
                        "node1_reference_productionsite",
                        e.target.value,
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>
                        {placeholderLabels.node1_reference_productionsite}
                      </em>
                    </MenuItem>
                    {(filterOptions.node1_reference_productionsite || []).map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node1_location}
                    onChange={(e) =>
                      handleTempFilterChange("node1_location", e.target.value)
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{placeholderLabels.node1_location}</em>
                    </MenuItem>
                    {(filterOptions.node1_location || []).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Divider orientation="vertical" className={classes.divider} />

              {/* Column 2: Node 2 Filters */}
              <div className={classes.filterColumn}>
                <div className={classes.columnTitle}>{t("node_2_title")}</div>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node2_reference_client}
                    onChange={(e) =>
                      handleTempFilterChange(
                        "node2_reference_client",
                        e.target.value,
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{placeholderLabels.node2_reference_client}</em>
                    </MenuItem>
                    {(filterOptions.node2_reference_client || []).map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node2_location}
                    onChange={(e) =>
                      handleTempFilterChange("node2_location", e.target.value)
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{placeholderLabels.node2_location}</em>
                    </MenuItem>
                    {(filterOptions.node2_location || []).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Divider orientation="vertical" className={classes.divider} />

              {/* Column 3: Node 3 Filters */}
              <div className={classes.filterColumn}>
                <div className={classes.columnTitle}>{t("node_3_title")}</div>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node3_reference_pointofsale}
                    onChange={(e) =>
                      handleTempFilterChange(
                        "node3_reference_pointofsale",
                        e.target.value,
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{placeholderLabels.node3_reference_pointofsale}</em>
                    </MenuItem>
                    {(filterOptions.node3_reference_pointofsale || []).map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
                <FormControl className={classes.formControl}>
                  <Select
                    value={tempFilters.node3_location}
                    onChange={(e) =>
                      handleTempFilterChange("node3_location", e.target.value)
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{placeholderLabels.node3_location}</em>
                    </MenuItem>
                    {(filterOptions.node3_location || []).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={classes.buttonsContainer}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClear}
                className={classes.clearButton}
              >
                {t("clear")}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                className={classes.applyButton}
              >
                {t("apply")}
              </Button>
            </div>
          </Box>
        </>
      )}
    </div>
  );
};

RouteFilter.propTypes = {
  clientColor: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    node1_reference_productionsite: PropTypes.array,
    node1_location: PropTypes.array,
    node2_reference_client: PropTypes.array,
    node2_location: PropTypes.array,
    node3_reference_pointofsale: PropTypes.array,
    node3_location: PropTypes.array,
  }).isRequired,
  currentFilters: PropTypes.shape({
    node1_reference_productionsite: PropTypes.string,
    node1_location: PropTypes.string,
    node2_reference_client: PropTypes.string,
    node2_location: PropTypes.string,
    node3_reference_pointofsale: PropTypes.string,
    node3_location: PropTypes.string,
  }).isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default RouteFilter;
